/**
 * AIWebhookService
 *
 * Business logic service for calling AI webhook via backend proxy.
 * Implements comprehensive error handling for all error types (FR-059):
 * - Timeout (>20min)
 * - Network errors
 * - 4xx HTTP responses
 * - 5xx HTTP responses
 * - status: 'error' in successful response
 *
 * Related: Sprint 6, FR-059
 * Reference: temp_plans/ai_strategy_creation_analysis.md
 */

import { getBackendUrl } from '../../../../frontend/src/config/backend';
import type { AIWebhookRequest } from './AIPayloadBuilder';

/**
 * Webhook response structure from backend /api/webhook-proxy
 */
export interface AIWebhookResponse {
  task_id?: string;
  status?: 'completed' | 'error';
  result?: unknown;
  message?: string;
  error?: string;
}

/**
 * Call result with success/error states
 */
export interface WebhookCallResult {
  success: boolean;
  data?: AIWebhookResponse;
  error?: string;
}

/**
 * Service for calling AI webhooks via backend proxy with error handling
 */
export class AIWebhookService {
  private readonly TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes

  /**
   * Get the backend proxy URL based on environment
   * - Development: http://localhost:8080/api/webhook-proxy
   * - Production: Cloud Run URL + /api/webhook-proxy
   */
  private getProxyUrl(): string {
    const backendUrl = getBackendUrl();
    return `${backendUrl}/api/webhook-proxy`;
  }

  /**
   * Call AI webhook via backend proxy
   *
   * Error handling (FR-059):
   * - Timeout: "AI request timed out. Please try again."
   * - Network: "Network connection failed. Check your internet connection."
   * - 4xx: "Invalid request to AI service. Please check your data."
   * - 5xx: "AI service error. Please try again later."
   * - status:'error': Display response.message
   *
   * @param request - Webhook request with url, payload, action_description
   * @returns Call result with success/error status
   */
  async callWebhook(request: AIWebhookRequest): Promise<WebhookCallResult> {
    // Log full request for debugging
    console.log('[AIWebhookService] 🚀 Calling AI webhook:', {
      url: request.url,
      action: request.action_description,
      payload: request.payload,
    });
    console.log('[AIWebhookService] 📦 Full request body:', JSON.stringify(request, null, 2));
    console.log('[AIWebhookService] 📏 Request size:', JSON.stringify(request).length, 'characters');

    // Track performance
    const startTime = performance.now();

    // Create abort controller for timeout
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), this.TIMEOUT_MS);

    try {
      // Get dynamic proxy URL based on environment
      const proxyUrl = this.getProxyUrl();

      // Send POST to backend proxy
      console.log('[AIWebhookService] 📡 Sending POST to:', proxyUrl);
      console.time('[AIWebhookService] ⏱️ Webhook Response Time');

      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request),
        signal: abortController.signal
      });

      const responseTime = performance.now() - startTime;
      console.timeEnd('[AIWebhookService] ⏱️ Webhook Response Time');
      console.log('[AIWebhookService] ⏱️ Exact time:', responseTime.toFixed(2), 'ms');

      // Clear timeout on response
      clearTimeout(timeoutId);

      console.log('[AIWebhookService] 📨 Response status:', response.status, response.statusText);
      console.log('[AIWebhookService] 📋 Response headers:', Object.fromEntries(response.headers.entries()));

      // Handle HTTP error responses (4xx, 5xx)
      if (!response.ok) {
        // Try to get error details from response body
        try {
          const errorBody = await response.text();
          console.error('[AIWebhookService] ❌ HTTP Error response body:', errorBody);
          console.error('[AIWebhookService] 📏 Error response size:', errorBody.length, 'characters');
        } catch (e) {
          console.error('[AIWebhookService] ❌ Could not read error response body');
        }
        return this.handleHttpError(response.status);
      }

      // Parse JSON response
      const responseText = await response.text();
      console.log('[AIWebhookService] 📏 Response size:', responseText.length, 'characters');

      const data: AIWebhookResponse = JSON.parse(responseText);
      console.log('[AIWebhookService] ✅ Response data:', data);

      // Check for application-level error (status: 'error' in 200 OK response)
      if (data.status === 'error') {
        console.error('[AIWebhookService] ❌ Application-level error:', data.message);
        return {
          success: false,
          error: data.message || 'Unknown error from AI service'
        };
      }

      // Success
      console.log('[AIWebhookService] ✅ Webhook call successful');
      return {
        success: true,
        data
      };
    } catch (error) {
      const errorTime = performance.now() - startTime;
      console.timeEnd('[AIWebhookService] ⏱️ Webhook Response Time');
      console.error('[AIWebhookService] ❌ Error after:', errorTime.toFixed(2), 'ms');

      // Clear timeout on error
      clearTimeout(timeoutId);

      // Handle different error types
      return this.handleFetchError(error);
    }
  }

  /**
   * Handle HTTP error responses (4xx, 5xx)
   */
  private handleHttpError(status: number): WebhookCallResult {
    if (status >= 400 && status < 500) {
      // 4xx - Bad Request
      return {
        success: false,
        error: 'Invalid request to AI service. Please check your data.'
      };
    }

    if (status >= 500) {
      // 5xx - Server Error
      return {
        success: false,
        error: 'AI service error. Please try again later.'
      };
    }

    // Unexpected status code
    return {
      success: false,
      error: `Unexpected HTTP error: ${status}`
    };
  }

  /**
   * Handle fetch errors (timeout, network)
   */
  private handleFetchError(error: unknown): WebhookCallResult {
    if (error instanceof Error) {
      // Timeout error (AbortController aborted)
      if (error.name === 'AbortError') {
        return {
          success: false,
          error: 'AI request timed out. Please try again.'
        };
      }

      // Network error (TypeError from fetch)
      if (error.name === 'TypeError') {
        return {
          success: false,
          error: 'Network connection failed. Check your internet connection.'
        };
      }

      // Generic error
      return {
        success: false,
        error: error.message || 'Unknown error occurred'
      };
    }

    // Non-Error object thrown
    return {
      success: false,
      error: 'Unknown error occurred'
    };
  }
}
